import React from 'react';

/**
 * ISO27001Section4 component displaying documentation recommendations for Section 4
 * @returns {JSX.Element}
 */
function ISO27001Section4() {
  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-6 text-gray-800">Section 4: Context of the Organization</h1>
      
      <div className="bg-white rounded-xl shadow-md p-8 border border-gray-100 mb-6">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">Documentation Recommendations for Clients</h2>
        <p className="text-gray-700 mb-6">
          This guide outlines Section 4 of ISO 27001, its requirements, and the specific documents 
          clients should create to demonstrate compliance.
        </p>
        
        <div className="bg-blue-50 rounded-lg p-6 mb-8 border-l-4 border-blue-500">
          <h3 className="text-xl font-bold mb-3 text-gray-800">Overview of Section 4</h3>
          <p className="text-gray-700">
            Section 4 "Context of the Organization" forms the foundation of an effective Information 
            Security Management System (ISMS). It requires organizations to understand their context, 
            identify interested parties, determine the scope of their ISMS, and establish the ISMS itself.
          </p>
        </div>
        
        <h2 className="text-2xl font-bold mb-4 text-gray-800">Section Breakdown and Document Recommendations</h2>
        
        <div className="mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow duration-300 mb-6">
            <h3 className="text-xl font-bold mb-3 text-gray-800">4.1 Understanding the organization and its context</h3>
            
            <div className="mb-4">
              <h4 className="font-bold text-gray-700 mb-2">Requirement:</h4>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>The organization shall determine external and internal issues that are relevant to its purpose and that affect its ability to achieve the intended outcome(s) of its information security management system.</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-gray-700 mb-2">Document Recommendation:</h4>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="font-bold text-blue-700 mb-2">01-ISMS Scope Document</p>
                <p className="text-gray-700 mb-2">Include a section that identifies and analyzes:</p>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>External issues (legal, technological, competitive, market, cultural, social, economic environments)</li>
                  <li>Internal issues (governance, organizational structure, roles, policies, objectives, resources, knowledge)</li>
                  <li>How these issues impact information security objectives</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow duration-300 mb-6">
            <h3 className="text-xl font-bold mb-3 text-gray-800">4.2 Understanding the needs and expectations of interested parties</h3>
            
            <div className="mb-4">
              <h4 className="font-bold text-gray-700 mb-2">Requirement:</h4>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>The organization determines:
                  <ul className="list-disc pl-6 mt-1">
                    <li>(a) interested parties that are relevant to the information security management system; and</li>
                    <li>(b) the requirements of these interested parties relevant to information security.</li>
                  </ul>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-gray-700 mb-2">Document Recommendations:</h4>
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="font-bold text-blue-700 mb-2">01-ISMS Scope Document</p>
                  <p className="text-gray-700">Include a reference to the Interested Parties Log</p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="font-bold text-blue-700 mb-2">Interested Parties Log</p>
                  <p className="text-gray-700 mb-2">Create a separate document that:</p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-1">
                    <li>Identifies all relevant stakeholders (e.g., customers, employees, regulators, partners)</li>
                    <li>Lists their security requirements and expectations</li>
                    <li>Documents how these requirements will be addressed</li>
                    <li>Includes a review schedule</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow duration-300 mb-6">
            <h3 className="text-xl font-bold mb-3 text-gray-800">4.3 Determining the scope of the information security management system</h3>
            
            <div className="mb-4">
              <h4 className="font-bold text-gray-700 mb-2">Requirement:</h4>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>The organization determines the boundaries and applicability of the information security management system to establish its scope and record this as documented information.</li>
                <li>When determining this scope, the organization considers:
                  <ul className="list-disc pl-6 mt-1">
                    <li>(a) the external and internal issues within the organization and its context (4.1);</li>
                    <li>(b) the needs and expectations of interested parties (4.2); and</li>
                    <li>(c) interfaces and dependencies between activities performed by the organization, and those that are performed by other organizations.</li>
                  </ul>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-gray-700 mb-2">Document Recommendation:</h4>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="font-bold text-blue-700 mb-2">01-ISMS Scope Document</p>
                <p className="text-gray-700 mb-2">Include clear definitions of:</p>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Physical locations covered</li>
                  <li>Organizational units included</li>
                  <li>Information systems and technology in scope</li>
                  <li>Business processes covered</li>
                  <li>Exclusions (if any) with justification</li>
                  <li>Interfaces and dependencies with other organizations (including Kolega)</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow duration-300 mb-6">
            <h3 className="text-xl font-bold mb-3 text-gray-800">4.4 Information security management system</h3>
            
            <div className="mb-4">
              <h4 className="font-bold text-gray-700 mb-2">Requirement:</h4>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>The organization shall establish, implement, maintain and continually improve an information security management system, in accordance with the requirements of this international standard.</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-gray-700 mb-2">Document Recommendations:</h4>
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="font-bold text-blue-700 mb-2">02-ISMS Policy</p>
                  <p className="text-gray-700 mb-2">Create a separate policy document that:</p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-1">
                    <li>Declares management commitment to information security</li>
                    <li>Outlines the ISMS framework</li>
                    <li>Defines roles and responsibilities</li>
                    <li>Establishes the process for continual improvement</li>
                  </ul>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="font-bold text-blue-700 mb-2">ISMS Governance Council Meeting Minutes</p>
                  <p className="text-gray-700">Maintain records of regular reviews and improvement activities</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-amber-50 rounded-lg p-6 mb-8 border-l-4 border-amber-500">
          <h3 className="text-xl font-bold mb-3 text-gray-800">Integration with Kolega Responsibilities</h3>
          <p className="text-gray-700 mb-4">
            While these documents fall under client responsibility according to the division of ISO 27001 
            implementation responsibilities, they should reference Kolega's technical controls where appropriate:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li>The ISMS Scope should acknowledge Kolega's role in managing the platform infrastructure</li>
            <li>The Interested Parties Log should include Kolega as a key interested party</li>
            <li>The ISMS Policy should reference Kolega's technical security controls</li>
            <li>The scope should clearly define the boundaries between client and Kolega responsibilities</li>
          </ul>
        </div>
        
        <div className="bg-green-50 rounded-lg p-6 border-l-4 border-green-500">
          <h3 className="text-xl font-bold mb-3 text-gray-800">Recommended Implementation Approach</h3>
          <ol className="list-decimal pl-6 text-gray-700 space-y-2">
            <li>Start with developing the ISMS Scope Document (including context analysis)</li>
            <li>Create the Interested Parties Log</li>
            <li>Develop the ISMS Policy</li>
            <li>Establish governance structure and begin maintaining meeting minutes</li>
          </ol>
          <p className="text-gray-700 mt-4">
            These foundational documents will guide all subsequent ISO 27001 implementation activities.
          </p>
        </div>
      </div>
    </div>
  );
}

export default ISO27001Section4;
